/**
 * Authenticate Service - Backend API Integration
 *
 * Handles all authenticate-related API calls to the backend
 */

import { post, get, put, del } from "../utils/api";

/**
 * Register a new user
 *
 * @param {Object} userData - User information
 * @param {string} userData.first_name - First name
 * @param {string} userData.last_name - Last name
 * @param {string} userData.email - Email
 * @param {string} userData.phone - Phone number
 * @param {string} userData.password - Password
 * @param {string} userData.role - Role (client or business)
 * @returns {Promise<Object>} Registered user
 */
export const registerUser = async (userData) => {
  try {
    const response = await post("/auth/register", userData);
    return {
      success: true,
      id: response.id,
      message: "User registered successfully",
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || "Failed to register user",
    };
  }
};

/**
 * Login user
 *
 * @param {Object} credentials - Login credentials
 * @param {string} credentials.email - Email
 * @param {string} credentials.password - Password
 * @returns {Promise<Object>} Login response
 */
export const loginUser = async (credentials) => {
  try {
    const response = await post("/auth/login", credentials);
    return {
      success: true,
      user: response.user,
      token: response.token,
      refreshToken: response.refreshToken,
      businesses: response.businesses,
      memberships: response.memberships,
      pendingInvitations: response.pendingInvitations,
      routing: response.routing,
      hasClientProfile: response.hasClientProfile,
      business: response.business,
      message: "Login successful",
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || "Login failed",
      code: error.code,
    };
  }
};

/**
 * Register or login user via social authentication (Google, GitHub, etc.)
 *
 * @param {Object} userData - User data
 * @param {string} idToken - ID token
 * @returns {Promise<Object>} Auth response
 */
export const socialAuth = async (userData, idToken) => {
  try {
    const response = await post("/auth/social", {
      ...userData,
      id_token: idToken,
    });

    return {
      success: true,
      user: response.user,
      token: response.token,
      refreshToken: response.refreshToken,
      businesses: response.businesses,
      memberships: response.memberships,
      pendingInvitations: response.pendingInvitations,
      routing: response.routing,
      hasClientProfile: response.hasClientProfile,
      isNewUser: response.isNewUser,
      message: "Authentication successful",
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || "Social authentication failed",
    };
  }
};

/**
 * Re-issue JWT from the server (control-plane tenant resolution, same as login).
 * Body optional; sends current Bearer token via api client.
 */
export const refreshSessionToken = async () => {
  try {
    const response = await post("/auth/refresh", {});
    return {
      success: true,
      user: response.user,
      token: response.token,
      message: response.message || "Session refreshed",
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || "Failed to refresh session",
    };
  }
};

export const getMe = async () => {
  try {
    const response = await get("/auth/me");
    return {
      success: true,
      user: response.user,
      business: response.business || null,
      businesses: response.businesses || [],
      memberships: response.memberships || [],
      pendingInvitations: response.pendingInvitations || [],
      routing: response.routing || null,
      hasClientProfile: !!response.hasClientProfile,
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || "Failed to get user",
    };
  }
};

/**
 * Refresh access token using a refresh token
 */
export const refreshAccessToken = async (rt) => {
  return post("/auth/refresh", { refreshToken: rt });
};

/**
 * Logout user (revokes all refresh tokens)
 */
export const logoutUser = async () => {
  return post("/auth/logout", {});
};

export const updateMe = async (data) => {
  try {
    const response = await put("/auth/me", data);
    return {
      success: true,
      user: response.user,
      message: "User updated successfully",
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || "Failed to update user",
    };
  }
};
